import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, UserX } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../../components/ui/dialog";
import { EDSButton } from "../../../../../components/eds/EDSButton";
import { User } from "../../../../../types/VenueGroup";
import { getInitials } from "../../../../../utils/getInitials";

export interface AssignToModalProps {
  open: boolean;
  users: User[];
  currentAssignedTo?: string;
  currentUserId?: string;
  onSave: (userId: string | undefined) => void;
  onCancel: () => void;
}

export const AssignToModal: React.FC<AssignToModalProps> = ({
  open,
  users,
  currentAssignedTo,
  currentUserId,
  onSave,
  onCancel
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    currentAssignedTo
  );
  const [isUnassignSelected, setIsUnassignSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedUserId(currentAssignedTo);
      setIsUnassignSelected(false);
      setSearchQuery("");
    }
  }, [open, currentAssignedTo]);

  const sortedUsers = useMemo(() => {
    if (!currentUserId) return users;
    return [...users].sort((a, b) => {
      if (a._id === currentUserId) return -1;
      if (b._id === currentUserId) return 1;
      return 0;
    });
  }, [users, currentUserId]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return sortedUsers;
    const query = searchQuery.toLowerCase();
    return sortedUsers.filter((user) =>
      user.full_name.toLowerCase().includes(query)
    );
  }, [sortedUsers, searchQuery]);

  const handleSelectUser = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setIsUnassignSelected(false);
  }, []);

  const handleSelectUnassign = useCallback(() => {
    setSelectedUserId(undefined);
    setIsUnassignSelected(true);
  }, []);

  const isSaveEnabled = !!selectedUserId || isUnassignSelected;

  const handleSave = useCallback(() => {
    if (isSaveEnabled) {
      onSave(selectedUserId);
    }
  }, [isSaveEnabled, selectedUserId, onSave]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onCancel();
      }
    },
    [onCancel]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign to</DialogTitle>
          <DialogDescription>
            Select a user to assign this enquiry to.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users..."
            aria-label="Search users"
            className="w-full pl-9 pr-3 py-2 rounded-md text-sm border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div
          className="flex flex-col max-h-64 overflow-y-auto border border-border rounded-md"
          aria-label="User list"
        >
          {!searchQuery.trim() && (
            <button
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors border-b border-border ${
                isUnassignSelected
                  ? "bg-accent text-accent-foreground font-medium"
                  : "hover:bg-accent/50"
              }`}
              onClick={handleSelectUnassign}
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-semibold leading-none shrink-0"
                aria-hidden="true"
              >
                <UserX size={14} />
              </span>
              <span className="truncate">Unassigned</span>
            </button>
          )}
          {filteredUsers.map((user) => {
            const isSelected =
              !isUnassignSelected && selectedUserId === user._id;
            return (
              <button
                key={user._id}
                type="button"
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors border-b border-border last:border-b-0 ${
                  isSelected
                    ? "bg-accent text-accent-foreground font-medium"
                    : "hover:bg-accent/50"
                }`}
                onClick={() => handleSelectUser(user._id)}
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold leading-none shrink-0"
                  aria-hidden="true"
                >
                  {user.initials || getInitials(user.full_name)}
                </span>
                <span className="truncate" title={user.full_name}>
                  {user.full_name}
                  {currentUserId === user._id && " (you)"}
                </span>
              </button>
            );
          })}
          {filteredUsers.length === 0 && (
            <p className="text-sm text-muted-foreground px-3 py-4 text-center">
              No users found
            </p>
          )}
        </div>

        <DialogFooter>
          <EDSButton variant="secondary" size="md" onClick={onCancel}>
            Cancel
          </EDSButton>
          <EDSButton
            variant="default"
            size="md"
            onClick={handleSave}
            disabled={!isSaveEnabled}
          >
            Save
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
